import type { AddPvdConfigRequest } from '@/../generated-client/models';
import { usePvdConfigFields, type PvdConfigFieldValues } from './usePvdConfigFields';

export function usePvdConfigCreate(initialConfig: AddPvdConfigRequest) {
  const initialValues: PvdConfigFieldValues = {
    specName: initialConfig.spec_name,
    voltage: initialConfig.voltage_allowed_settings,
    current: initialConfig.current_allowed_settings,
    adaptiveVoltageAllowed: initialConfig.adaptive_voltage_allowed,
    adaptiveCurrentAllowed: initialConfig.adaptive_current_allowed,

    autoAdaptIsLocked: initialConfig.adaptive_shared_auto_adapt_is_locked,
    autoAdaptLockedToValue: initialConfig.adaptive_shared_auto_adapt_locked_to_value,

    voltageBubbleSizeIsLocked: initialConfig.adaptive_voltage_next_alert_size_is_locked,
    voltageBubbleSizeLockedToValue: initialConfig.adaptive_voltage_next_alert_size_locked_to_value,
    // initialConfig carries 1-based UI labels (from the factory or a UI-seeded source).
    // No +1 conversion needed here — values are already in slider (UI-label) space.
    voltageMinSetting: initialConfig.adaptive_voltage_min_setting,
    voltageMaxSetting: initialConfig.adaptive_voltage_max_setting,

    currentBubbleSizeIsLocked: initialConfig.adaptive_current_next_alert_size_is_locked,
    currentBubbleSizeLockedToValue: initialConfig.adaptive_current_next_alert_size_locked_to_value,
    currentMinSetting: initialConfig.adaptive_current_min_setting,
    currentMaxSetting: initialConfig.adaptive_current_max_setting,
  };

  const fields = usePvdConfigFields(initialValues);

  const submitConfig = (): AddPvdConfigRequest => ({
    org_code: initialConfig.org_code,
    spec_name: fields.specName,
    is_compass_core: initialConfig.is_compass_core,

    voltage_allowed_settings: fields.voltage,
    adaptive_voltage_allowed: fields.adaptiveVoltageAllowed,
    current_allowed_settings: fields.current,
    adaptive_current_allowed: fields.adaptiveCurrentAllowed,

    adaptive_shared_auto_adapt_is_locked: fields.autoAdaptIsLocked,
    adaptive_shared_auto_adapt_locked_to_value: fields.autoAdaptLockedToValue,

    adaptive_voltage_next_alert_size_is_locked: fields.voltageBubbleSizeIsLocked,
    adaptive_voltage_next_alert_size_locked_to_value: fields.voltageBubbleSizeLockedToValue,
    // Slider holds 1-based UI labels; API expects 0-based firmware indices. -1 converts.
    // API constraint: voltage [0, 21], current [0, 10].
    adaptive_voltage_min_setting: fields.voltageMinSetting - 1,
    adaptive_voltage_max_setting: fields.voltageMaxSetting - 1,

    adaptive_current_next_alert_size_is_locked: fields.currentBubbleSizeIsLocked,
    adaptive_current_next_alert_size_locked_to_value: fields.currentBubbleSizeLockedToValue,
    adaptive_current_min_setting: fields.currentMinSetting - 1,
    adaptive_current_max_setting: fields.currentMaxSetting - 1,

    should_force_apply_this_update: false,
  });

  return { ...fields, submitConfig };
}
